import { SxProps, Theme } from '@mui/material';
import { EntityBase } from '@provision/entity-types';
import { memo, ReactElement, useMemo } from 'react';
import { CustomViewAttribute } from 'src/graphql/entities/custom-view';
import { CustomAttributeColumnRenderer } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import { EntityNames } from 'src/graphql/types';
import { customComponentsMap } from 'src/pages/CustomViews/custom-views-entities-table/custom-components-map';
import type { TableSelectionState } from 'src/pages/CustomViews/custom-views-entities-table/custom-views-entities-table';
import { RowSelectCheckbox } from 'src/pages/CustomViews/custom-views-entities-table/row-select-checkbox';
import type { RecordClickHandler } from 'src/pages/CustomViews/hooks/use-debounced-record-click';
import { CardSlotContent } from './card-slot-content';
import { ChipLineCardBody } from './chip-line-card-body';
import { ConversationCardSwipe } from './conversation-card-swipe';
import {
  CARD_LAYOUT_BY_ENTITY,
  CardLayoutEntity,
  CardLayoutKind,
  splitCardColumns,
} from './mobile-card-layout';
import { CardRoot, CardSlot } from './mobile-card-list.elements';
import { TwoColumnCardBody } from './two-column-card-body';
import { useCardInteraction } from './use-card-interaction';
import type { SwipeableConversation } from './use-conversation-swipe-actions';

// An unselected card keeps its checkbox out of the layout, over the card's left padding: the
// input stays reachable and takes no width, and the checkbox column only appears once a
// selection is under way.
const hiddenCheckboxSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  width: (theme) => theme.spacing(2),
  padding: 0,
  borderRadius: 0,
  opacity: 0,
};

const visibleCheckboxSx: SxProps<Theme> = {
  flexShrink: 0,
  marginLeft: '-0.25rem',
  opacity: 1,
};

interface RecordCardContentProps<T extends EntityBase> {
  record: T;
  entityName: CardLayoutEntity;
  columns: CustomViewAttribute[];
  allAttributes?: CustomViewAttribute[];
  isActive: boolean;
  isSelectionMode: boolean;
  selectionState?: TableSelectionState;
  customAttributeRenderers?: Map<string, CustomAttributeColumnRenderer>;
  onClick?: RecordClickHandler<T>;
  isSwiping?: () => boolean;
}

const isSwipeableConversation = <T extends EntityBase>(
  entityName: CardLayoutEntity,
  record: T,
): record is T & SwipeableConversation =>
  entityName === EntityNames.Conversation && 'conversationId' in record;

const RecordCardContent = <T extends EntityBase>({
  record,
  entityName,
  columns,
  allAttributes,
  isActive,
  isSelectionMode,
  selectionState,
  customAttributeRenderers,
  onClick,
  isSwiping,
}: RecordCardContentProps<T>) => {
  const layout = CARD_LAYOUT_BY_ENTITY[entityName];
  const customComponentsByAttribute = customComponentsMap[entityName];

  const cardColumns = useMemo(
    () => splitCardColumns(columns, layout, allAttributes),
    [columns, layout, allAttributes],
  );

  const { isSelected, rootProps } = useCardInteraction<T>({
    record,
    isSelectionMode,
    selectionState,
    onOpen: onClick,
    isSwiping,
  });

  return (
    <CardRoot
      role="button"
      tabIndex={0}
      isActive={isActive}
      hasMinHeight={layout.kind === CardLayoutKind.ChipLine}
      aria-current={isActive || undefined}
      aria-pressed={isSelectionMode ? isSelected : undefined}
      data-testid={`custom-views-mobile-card-${record.id}`}
      {...rootProps}
    >
      {selectionState ? (
        <RowSelectCheckbox
          id={String(record.id)}
          selectionState={selectionState}
          sx={isSelectionMode ? visibleCheckboxSx : hiddenCheckboxSx}
        />
      ) : null}
      {layout.slot ? (
        <CardSlot>
          <CardSlotContent
            record={record}
            layout={layout}
            cardColumns={cardColumns}
            customComponentsByAttribute={customComponentsByAttribute}
          />
        </CardSlot>
      ) : null}
      {layout.kind === CardLayoutKind.TwoColumn ? (
        <TwoColumnCardBody
          record={record}
          layout={layout}
          cardColumns={cardColumns}
          customAttributeRenderers={customAttributeRenderers}
          customComponentsByAttribute={customComponentsByAttribute}
        />
      ) : (
        <ChipLineCardBody
          record={record}
          layout={layout}
          cardColumns={cardColumns}
          customAttributeRenderers={customAttributeRenderers}
          customComponentsByAttribute={customComponentsByAttribute}
        />
      )}
    </CardRoot>
  );
};

const MobileRecordCardComp = <T extends EntityBase>({
  isSwipeOpen = false,
  onSwipeOpenChange,
  onSwipeStart,
  ...cardProps
}: Omit<RecordCardContentProps<T>, 'isSwiping'> & {
  isSwipeOpen?: boolean;
  onSwipeOpenChange?: (id: string, isOpen: boolean) => void;
  onSwipeStart?: (id: string) => void;
}) => {
  const { record, entityName, isSelectionMode } = cardProps;

  if (!onSwipeOpenChange || !isSwipeableConversation(entityName, record)) {
    return <RecordCardContent<T> {...cardProps} />;
  }

  const id = String(record.id);

  return (
    <ConversationCardSwipe
      conversation={record}
      isOpen={isSwipeOpen}
      isSelectionMode={isSelectionMode}
      onOpenChange={(isOpen) => onSwipeOpenChange(id, isOpen)}
      onSwipeStart={() => onSwipeStart?.(id)}
      renderCard={(isSwiping) => <RecordCardContent<T> {...cardProps} isSwiping={isSwiping} />}
    />
  );
};

export const MobileRecordCard = memo(MobileRecordCardComp) as <T extends EntityBase>(
  props: React.ComponentProps<typeof MobileRecordCardComp<T>>,
) => ReactElement;
